import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Coins, Package, CreditCard, Gift, Loader2 } from "lucide-react";
import { useState } from "react";
import { useLanguage } from "@/i18n";

const CREDIT_COSTS = {
  chat: 1,
  image: 10,
  file_analysis: 3,
  tts: 2,
  stt: 2
};

export const CreditModal = ({ 
  open, 
  onOpenChange, 
  credits,
  creditPackages,
  onPurchaseWithStripe,
  onPurchaseDemo,
  isProcessingPayment
}) => {
  const { t } = useLanguage();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [creditAmount, setCreditAmount] = useState(100);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Coins size={20} className="text-yellow-400" /> {t('buy_credits')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-zinc-800 rounded-lg p-4">
            <p className="text-zinc-400 text-sm">{t('credits')}</p>
            <p className="text-3xl font-bold text-yellow-400">{credits} <span className="text-sm text-zinc-500">{t('credits').toLowerCase()}</span></p>
          </div>
          
          <div>
            <p className="text-zinc-400 text-sm mb-2">{t('credits')}</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="bg-zinc-800 p-2 rounded flex justify-between">
                <span className="text-zinc-400">💬 {t('mode_chat')}</span>
                <span className="text-white">{CREDIT_COSTS.chat} {t('credits').toLowerCase()}</span>
              </div>
              <div className="bg-zinc-800 p-2 rounded flex justify-between">
                <span className="text-zinc-400">🖼️ {t('mode_image')}</span>
                <span className="text-white">{CREDIT_COSTS.image} {t('credits').toLowerCase()}</span>
              </div>
              <div className="bg-zinc-800 p-2 rounded flex justify-between">
                <span className="text-zinc-400">📄 {t('mode_file')}</span>
                <span className="text-white">{CREDIT_COSTS.file_analysis} {t('credits').toLowerCase()}</span>
              </div>
              <div className="bg-zinc-800 p-2 rounded flex justify-between">
                <span className="text-zinc-400">🔊 {t('voice_on')}</span>
                <span className="text-white">{CREDIT_COSTS.tts} {t('credits').toLowerCase()}</span>
              </div>
            </div>
          </div>
          
          {/* Stripe Payment Packages */}
          <div>
            <p className="text-zinc-400 text-sm mb-2">{t('subscription')}</p>
            <div className="grid grid-cols-2 gap-3">
              {creditPackages.map((pkg) => (
                <button
                  key={pkg.id}
                  onClick={() => setSelectedPackage(pkg.id)}
                  className={`p-4 rounded-lg border transition-all text-left ${
                    selectedPackage === pkg.id 
                      ? "border-green-500 bg-green-500/10" 
                      : "border-zinc-700 hover:border-zinc-600"
                  }`}
                  data-testid={`package-${pkg.id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Package size={16} className={selectedPackage === pkg.id ? "text-green-400" : "text-zinc-400"} />
                    <span className="font-semibold text-white">{pkg.name_key && t(pkg.name_key) !== pkg.name_key ? t(pkg.name_key) : pkg.name}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-yellow-400 font-bold">{pkg.credits} {t('credits').toLowerCase()}</span>
                    <span className="text-green-400 text-sm">₺{pkg.price}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
          
          <Button 
            onClick={() => selectedPackage && onPurchaseWithStripe(selectedPackage)} 
            disabled={!selectedPackage || isProcessingPayment}
            className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50" 
            data-testid="buy-with-stripe-btn"
          >
            {isProcessingPayment ? (
              <><Loader2 size={18} className="mr-2 animate-spin" /> {t('loading')}</>
            ) : (
              <><CreditCard size={18} className="mr-2" /> Stripe</>
            )}
          </Button>
          
          <div className="border-t border-zinc-800 pt-4">
            <p className="text-zinc-500 text-xs mb-2 text-center">{t('or')} demo:</p>
            <div className="grid grid-cols-4 gap-2 mb-2">
              {[50, 100, 250, 500].map((amount) => (
                <button
                  key={amount}
                  onClick={() => setCreditAmount(amount)}
                  className={`p-2 rounded-lg border transition-all text-sm ${
                    creditAmount === amount 
                      ? "border-yellow-500 bg-yellow-500/10 text-yellow-400" 
                      : "border-zinc-700 hover:border-zinc-600 text-zinc-300"
                  }`}
                  data-testid={`credit-${amount}`}
                >
                  {amount}
                </button>
              ))}
            </div>
            <Button 
              onClick={() => onPurchaseDemo(creditAmount)} 
              variant="outline" 
              className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800" 
              data-testid="buy-credits-btn"
            >
              <Gift size={18} className="mr-2" /> Demo: {creditAmount} {t('credits')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
